/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as Msdyn365 from '@msdyn365-commerce/core';
import axios from 'axios';
/**
 * BedhubHeaderDataAction Input Action
 */
export class BedhubHeaderInput implements Msdyn365.IActionInput {
  constructor() { }
  public getCacheKey = () => 'BedhubHeader';
  public getCacheObjectType = () => 'BedhubHeader';
  public dataCacheType = (): Msdyn365.CacheType => 'application';
}

const createInput = (args: Msdyn365.ICreateActionContext<Msdyn365.IGeneric<Msdyn365.IAny>>): Msdyn365.IActionInput => {
  // Ensure Bedhub API is Enable
  if (args.config && !args.config.bedhubDataSource) {
    throw new Error('Cant call BEDHUB API, as its turn off');
  } else {
    return new BedhubHeaderInput();
  }
};

async function getAccessToken(baseURL: string, ctx: Msdyn365.IRequestContext) {
  const clientId: Msdyn365.ISecretValue | undefined = await Msdyn365.msdyn365Commerce.secretManager?.getSecretValue(
    'bh-client-id',
    ctx.apiSettings.baseUrl
  );
  const clientSecret: Msdyn365.ISecretValue | undefined = await Msdyn365.msdyn365Commerce.secretManager?.getSecretValue(
    'bh-client-secret',
    ctx.apiSettings.baseUrl
  );
  const tenant: Msdyn365.ISecretValue | undefined = await Msdyn365.msdyn365Commerce.secretManager?.getSecretValue(
    'bh-tenant-id',
    ctx.apiSettings.baseUrl
  );

  if (process.env.CURRENT_ENVIRONMENT === 'node') {
    const msalConfig = {
      auth: {
        authority: new URL(tenant?.value ?? '', 'https://login.microsoftonline.com').href,
        clientId: clientId?.value ?? '',
        clientSecret: clientSecret?.value ?? '',
        tenantId: tenant?.value ?? ''
      }
    };
    // eslint-disable-next-line @typescript-eslint/no-require-imports
    const ConfidentialClientApplication = require('@azure/msal-node').ConfidentialClientApplication;

    const msalInstance = new ConfidentialClientApplication(msalConfig);
    const token = await msalInstance.acquireTokenByClientCredential({ scopes: [new URL('.default', baseURL).href] });
    return token?.accessToken;
  }
  return '';
}
async function getBedhubHeaderData(input: BedhubHeaderInput, ctx: Msdyn365.IActionContext): Promise<any> {
  const baseURL = ctx?.requestContext?.app?.config?.bedhubBaseUrl;
  let token = '';
  try {
    token = await getAccessToken(baseURL, ctx.requestContext);
  } catch (error) {
    console.error('error for getting bedhub authorization token [getAccessToken]', error);
  }
  const graphqlURL = new URL('graphql', baseURL).href;

  try {
    // Query to get the GRAPHQL Data
    const query = `
        {
          ui {
            header {
              account {
                myAccount {
                  ariaLabel
                  label
                  type
                  url
                }
                signIn {
                  ariaLabel
                  label
                  type
                  url
                }
                signOut {
                  ariaLabel
                  label
                  type
                  url
                }
                signUp {
                  ariaLabel
                  label
                  type
                  url
                }
              }
              accountMenu {
                url
                type
                signedIn
                label
                ariaLabel
                callout
              }
              search {
                icon {
                  url
                }
                placeholder
                url
              }
              actions {
                image {
                  url
                }
                label
                mobile
                desktop
                type
                url
                beforeLogo
                ariaLabel
              }
            }
            navbar {
              products {
                ... on UIProductMenu {
                  __typename
                  action {
                    ariaLabel
                    label
                    type
                    url
                  }
                  badge {
                    label
                    type
                  }
                  callout
                  categories {
                    label
                    items {
                      ariaLabel
                      image {
                        url
                      }
                      label
                      type
                      url
                    }
                    accordion {
                      less {
                        ariaLabel
                        label
                      }
                      maximum
                      more {
                        ariaLabel
                        label
                      }
                    }
                  }
                  label
                  promotion {
                    ariaLabel
                    image {
                      url
                    }
                    label
                    type
                    url
                  }
                  subcategories {
                    items {
                      ariaLabel
                      badge {
                        label
                        type
                      }
                      image {
                        url
                      }
                      label
                      type
                      url
                    }
                    label
                  }
                }
                ... on UIProductMenuLink {
                  __typename
                  ariaLabel
                  type
                  badge {
                    label
                    type
                  }
                  callout
                  html
                  label
                  url
                }
              }
              utilities {
                ariaLabel
                label
                type
                url
              }
            }
            pencilBanner {
              interval
              items {
                ariaLabel
                countdown {
                  end
                  start
                }
                html
                label
                schedule {
                  end
                  start
                }
                type
                url
              }
              next {
                ariaLabel
                image {
                  url
                }
                label
              }
              previous {
                ariaLabel
                image {
                  url
                }
                label
              }
            }
          }
        }
        `;
    const response = await axios.post(
      graphqlURL,
      JSON.stringify({
        query: query
      }),
      {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        }
      }
    );
    if (response?.data) {
      return response.data;
    }
    return {};
  } catch (error) {
    console.error('error for bedhub header [getBedhubHeaderData]', error);
    return {};
  }
}

export default Msdyn365.createObservableDataAction({
  action: <Msdyn365.IAction<{}>>getBedhubHeaderData,
  id: 'BedhubHeader',
  input: createInput
});
